import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';
import * as FiIcons from 'react-icons/fi';
import { SiGooglemaps, SiApple, SiWaze } from 'react-icons/si';
import SafeIcon from '../../common/SafeIcon';

const { FiWifi, FiKey, FiMapPin, FiBook, FiStar, FiPhone, FiInfo, FiChevronRight, FiHome, FiUser, FiCalendar, FiShield, FiLogOut, FiMap, FiTv, FiCoffee, FiAlertCircle, FiLock, FiChevronDown, FiCopy, FiCheck, FiWind, FiDroplet, FiThermometer, FiZap, FiPlus, FiMinus } = FiIcons;

const PortalContent = ({ guestPage, property, onLogout }) => {
  const [showMapOptions, setShowMapOptions] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedSections, setExpandedSections] = useState({});
  const [copiedText, setCopiedText] = useState('');
  const [imageError, setImageError] = useState(false);

  // Scroll spy to update active tab based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      const sections = ['overview', 'access', 'wifi', 'rules', 'guide', 'recommendations', 'support'];
      const scrollPosition = window.scrollY + 150;
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element && element.offsetTop <= scrollPosition && (element.offsetTop + element.offsetHeight) > scrollPosition) {
          setActiveTab(section);
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const headerOffset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({ 
        top: offsetPosition, 
        behavior: 'smooth' 
      });
      setActiveTab(id);
    }
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopiedText(label);
    toast.success(`${label} copied!`);
    setTimeout(() => setCopiedText(''), 2000);
  };

  const openMap = (service) => {
    if (!property.address) {
      toast.error('Property address is missing');
      return;
    }
    const encodedAddress = encodeURIComponent(property.address);
    let url = '';
    switch (service) {
      case 'google': url = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`; break;
      case 'apple': url = `https://maps.apple.com/?q=${encodedAddress}`; break;
      case 'waze': url = `https://waze.com/ul?q=${encodedAddress}`; break;
      default: return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Check if content is HTML
  const isHTML = (text) => {
    if (!text || typeof text !== 'string') return false;
    // Check for HTML tags - more comprehensive pattern
    const htmlPattern = /<\/?[a-z][\s\S]*?>/i;
    // Also check for common HTML entities or rich text editor output
    const hasHtmlTags = htmlPattern.test(text);
    // Check if it looks like HTML from rich text editor (contains tags like <p>, <br>, <strong>, etc.)
    const richTextPattern = /<(p|div|span|br|strong|em|ul|ol|li|h[1-6]|a|img)[\s\S]*?>/i;
    return hasHtmlTags || richTextPattern.test(text);
  };

  // Parse text into structured format (for plain text)
  const parseTextContent = (text) => {
    if (!text) return [];
    // If it's HTML, return it as-is for HTML rendering
    if (isHTML(text)) {
      return [{ type: 'html', content: text }];
    }
    
    const lines = text.split('\n').filter(line => line.trim());
    const sections = [];
    let currentSection = null;
    
    lines.forEach(line => {
      const trimmed = line.trim();
      // Check for numbered lists (1., 2., etc.)
      const numberedMatch = trimmed.match(/^(\d+)\.\s*(.+)$/);
      // Check for bullet points
      const bulletMatch = trimmed.match(/^[•\-*]\s*(.+)$/);
      // Check for section headers (lines ending with :)
      const headerMatch = trimmed.match(/^(.+):\s*$/);
      // Check for bold/important text (lines with ** or ALL CAPS)
      const importantMatch = trimmed.match(/^\*\*(.+)\*\*$/) || (trimmed === trimmed.toUpperCase() && trimmed.length > 3 && trimmed.length < 50);
      
      if (headerMatch) {
        if (currentSection) sections.push(currentSection);
        currentSection = { type: 'header', text: headerMatch[1], items: [] };
      } else if (numberedMatch) {
        if (!currentSection) currentSection = { type: 'list', items: [] };
        currentSection.items.push({ type: 'numbered', text: numberedMatch[2], number: numberedMatch[1] });
      } else if (bulletMatch) {
        if (!currentSection) currentSection = { type: 'list', items: [] };
        currentSection.items.push({ type: 'bullet', text: bulletMatch[1] });
      } else if (importantMatch) {
        if (!currentSection) currentSection = { type: 'list', items: [] };
        currentSection.items.push({ type: 'important', text: importantMatch[1] || trimmed });
      } else {
        if (!currentSection) currentSection = { type: 'list', items: [] };
        currentSection.items.push({ type: 'paragraph', text: trimmed });
      }
    });
    
    if (currentSection) sections.push(currentSection);
    return sections.length > 0 ? sections : [{ type: 'paragraph', text }];
  };

  // Reusable Card Component
  const SectionCard = ({ id, title, icon, children, className = "" }) => (
    <div 
      id={id} 
      className={`bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden mb-8 scroll-mt-28 ${className}`}
    >
      <div className="p-6 border-b border-gray-100 flex items-center space-x-3 bg-gradient-to-r from-gray-50 to-white">
        <div className="p-2.5 bg-blue-100 rounded-xl text-blue-600 shadow-sm">
          <SafeIcon icon={icon} className="text-xl" />
        </div>
        <h2 className="text-xl font-bold text-gray-900">{title}</h2>
      </div>
      <div className="p-6">
        {children}
      </div>
    </div>
  );

  // Info Card Component
  const InfoCard = ({ icon, title, children, color = "blue", className = "" }) => {
    const colorClasses = {
      blue: "bg-blue-50 border-blue-200 text-blue-900",
      green: "bg-green-50 border-green-200 text-green-900",
      orange: "bg-orange-50 border-orange-200 text-orange-900",
      purple: "bg-purple-50 border-purple-200 text-purple-900",
      yellow: "bg-yellow-50 border-yellow-200 text-yellow-900",
      red: "bg-red-50 border-red-200 text-red-900"
    };
    return (
      <div className={`rounded-xl p-5 border-2 ${colorClasses[color]} ${className}`}>
        {title && (
          <div className="flex items-center space-x-2 mb-3">
            {icon && <SafeIcon icon={icon} className="text-lg" />}
            <h3 className="font-bold text-sm uppercase tracking-wide">{title}</h3>
          </div>
        )}
        {children}
      </div>
    );
  };

  // Collapsible Section Component
  const CollapsibleSection = ({ id, title, icon, children, defaultExpanded = false }) => {
    const isExpanded = expandedSections[id] !== undefined ? expandedSections[id] : defaultExpanded;
    return (
      <div className="border border-gray-200 rounded-xl overflow-hidden mb-4">
        <button
          onClick={() => toggleSection(id)}
          className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center space-x-3">
            {icon && <SafeIcon icon={icon} className="text-gray-600" />}
            <h3 className="font-semibold text-gray-900 text-left">{title}</h3>
          </div>
          <SafeIcon 
            icon={isExpanded ? FiMinus : FiPlus} 
            className="text-gray-500 transition-all" 
          />
        </button>
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className="p-4 bg-white border-t border-gray-100">
                {children}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  // Render parsed content
  const renderParsedContent = (sections) => {
    // If content is HTML, render it directly
    if (sections.length === 1 && sections[0].type === 'html') {
      return (
        <div 
          key="html-content"
          className="prose prose-sm max-w-none"
          dangerouslySetInnerHTML={{ __html: sections[0].content }}
          style={{
            color: '#374151',
            lineHeight: '1.75'
          }}
        />
      );
    }
    
    // If sections is empty or undefined, return empty
    if (!sections || sections.length === 0) {
      return null;
    }
    
    return sections.map((section, idx) => {
      if (section.type === 'header') {
        return (
          <div key={idx} className="mb-4">
            <h4 className="font-bold text-gray-900 mb-3 text-base">
              <span className="font-bold">{section.text}:</span>
            </h4>
            {section.items.length > 0 && (
              <div className="ml-4 space-y-2">
                {section.items.map((item, itemIdx) => (
                  <div key={itemIdx} className="text-gray-700 text-sm leading-relaxed">
                    {item.type === 'numbered' && (
                      <div className="flex items-start space-x-2">
                        <span className="font-semibold text-blue-600 mt-0.5">{item.number}.</span>
                        <span>
                          {item.text.split(/([^:]+:)/).map((part, partIdx) => {
                            if (part.endsWith(':')) {
                              return <span key={partIdx} className="font-bold">{part}</span>;
                            }
                            return <span key={partIdx}>{part}</span>;
                          })}
                        </span>
                      </div>
                    )}
                    {item.type === 'bullet' && (
                      <div className="flex items-start space-x-2">
                        <span className="text-blue-600 mt-1">•</span>
                        <span>
                          {item.text.split(/([^:]+:)/).map((part, partIdx) => {
                            if (part.endsWith(':')) {
                              return <span key={partIdx} className="font-bold">{part}</span>;
                            }
                            return <span key={partIdx}>{part}</span>;
                          })}
                        </span>
                      </div>
                    )}
                    {item.type === 'important' && (
                      <div className="font-semibold text-gray-900 bg-yellow-50 px-3 py-1 rounded-md inline-block">
                        {item.text}
                      </div>
                    )}
                    {item.type === 'paragraph' && (
                  <p>
                    {item.text.split(/([^:]+:)/).map((part, partIdx) => {
                      if (part.endsWith(':')) {
                        return <span key={partIdx} className="font-bold">{part}</span>;
                      }
                      return <span key={partIdx}>{part}</span>;
                    })}
                  </p>
                )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      } else {
        return (
          <div key={idx} className="space-y-2">
            {section.items.map((item, itemIdx) => (
              <div key={itemIdx} className="text-gray-700 text-sm leading-relaxed">
                {item.type === 'numbered' && (
                  <div className="flex items-start space-x-3 mb-2">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-700 font-semibold text-xs flex items-center justify-center mt-0.5">
                      {item.number}
                    </span>
                    <span className="flex-1">
                      {item.text.split(/([^:]+:)/).map((part, partIdx) => {
                        if (part.endsWith(':')) {
                          return <span key={partIdx} className="font-bold">{part}</span>;
                        }
                        return <span key={partIdx}>{part}</span>;
                      })}
                    </span>
                  </div>
                )}
                {item.type === 'bullet' && (
                  <div className="flex items-start space-x-2 mb-2">
                    <span className="text-blue-600 mt-1.5">•</span>
                    <span>
                      {item.text.split(/([^:]+:)/).map((part, partIdx) => {
                        if (part.endsWith(':')) {
                          return <span key={partIdx} className="font-bold">{part}</span>;
                        }
                        return <span key={partIdx}>{part}</span>;
                      })}
                    </span>
                  </div>
                )}
                {item.type === 'important' && (
                  <div className="font-semibold text-gray-900 bg-yellow-50 px-3 py-2 rounded-md mb-2 border-l-4 border-yellow-400">
                    {item.text}
                  </div>
                )}
                {item.type === 'paragraph' && (
                  <p className="mb-2">
                    {item.text.split(/([^:]+:)/).map((part, partIdx) => {
                      if (part.endsWith(':')) {
                        return <span key={partIdx} className="font-bold">{part}</span>;
                      }
                      return <span key={partIdx}>{part}</span>;
                    })}
                  </p>
                )}
              </div>
            ))}
          </div>
        );
      }
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-gray-50 pb-20 font-sans">
      {/* Sticky Navigation Header */}
      <div className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-gray-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex justify-between items-center gap-4">
            <div className="flex overflow-x-auto no-scrollbar gap-2 flex-1 pb-1 sm:pb-0">
              {[
                { id: 'overview', icon: FiHome, label: 'Overview' },
                { id: 'access', icon: FiKey, label: 'Access' },
                { id: 'wifi', icon: FiWifi, label: 'WiFi' },
                { id: 'rules', icon: FiBook, label: 'Rules' },
                { id: 'guide', icon: FiStar, label: 'Guide' },
                { id: 'recommendations', icon: FiMapPin, label: 'Local' },
                { id: 'support', icon: FiPhone, label: 'Support' },
              ].map(item => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all flex-shrink-0 ${
                    activeTab === item.id 
                      ? 'bg-blue-600 text-white shadow-md' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <SafeIcon icon={item.icon} />
                  <span className="hidden sm:inline">{item.label}</span>
                </button>
              ))}
            </div>
            <button 
              onClick={onLogout} 
              className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
              title="Logout"
            >
              <SafeIcon icon={FiLogOut} className="text-xl" />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        
        {/* 1. Overview Section */}
        <section id="overview" className="scroll-mt-32 space-y-6">
          <div 
            className="bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 rounded-3xl p-8 text-white shadow-xl relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 -mr-12 -mt-12 w-40 h-40 bg-white opacity-10 rounded-full blur-2xl"></div>
            <div className="absolute bottom-0 left-0 -ml-12 -mb-12 w-32 h-32 bg-white opacity-10 rounded-full blur-2xl"></div>
            
            <div className="relative z-10 text-center">
              <div className="flex justify-center mb-4">
                {property.image && !imageError ? (
                  <div className="w-[100px] h-[100px] rounded-full overflow-hidden border-4 border-white/30 shadow-lg bg-white/10">
                    <img 
                      src={property.image} 
                      alt={property.name}
                      className="w-full h-full object-cover"
                      onError={() => setImageError(true)}
                    />
                  </div>
                ) : (
                  <div className="w-[100px] h-[100px] rounded-full bg-white/20 border-4 border-white/30 shadow-lg flex items-center justify-center">
                    <SafeIcon icon={FiHome} className="text-4xl text-blue-200" />
                  </div>
                )}
              </div>
              <h1 className="text-4xl font-bold mb-2">Welcome to {property.name}</h1>
              <p className="text-blue-100 text-lg mb-8">Hello {guestPage.guestName}, we're thrilled to have you!</p>
              
              <div className="grid grid-cols-2 gap-4 max-w-lg mx-auto">
                <div className="bg-white/15 backdrop-blur-sm rounded-xl p-5 border border-white/20 shadow-lg">
                  <div className="flex items-center justify-center space-x-2 text-blue-200 mb-2">
                    <SafeIcon icon={FiCalendar} />
                    <span className="text-xs uppercase tracking-wider font-semibold">Check-in</span>
                  </div>
                  <p className="text-3xl font-bold">{format(new Date(guestPage.checkInDate), 'MMM d')}</p>
                  <p className="text-sm opacity-90 mt-1">{property.checkInTime || '3:00 PM'}</p>
                </div>
                <div className="bg-white/15 backdrop-blur-sm rounded-xl p-5 border border-white/20 shadow-lg">
                  <div className="flex items-center justify-center space-x-2 text-blue-200 mb-2">
                    <SafeIcon icon={FiLogOut} />
                    <span className="text-xs uppercase tracking-wider font-semibold">Check-out</span>
                  </div>
                  <p className="text-3xl font-bold">{format(new Date(guestPage.checkOutDate), 'MMM d')}</p>
                  <p className="text-sm opacity-90 mt-1">{property.checkOutTime || '11:00 AM'}</p>
                </div>
              </div>
            </div>
          </div>
             
          {guestPage.specialInstructions && (
            <div 
              className="bg-gradient-to-r from-yellow-50 to-amber-50 border-2 border-yellow-200 rounded-xl p-6 shadow-sm"
            >
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <SafeIcon icon={FiAlertCircle} className="text-yellow-600 text-3xl" />
                </div>
                <div>
                  <h3 className="font-bold text-yellow-900 mb-2 text-lg">Special Instructions</h3>
                  <p className="text-yellow-800 text-sm leading-relaxed whitespace-pre-line">{guestPage.specialInstructions}</p>
                </div>
              </div>
            </div>
          )}
        </section>

        {/* 2. Access Section */}
        <SectionCard id="access" title="Access & Directions" icon={FiKey}>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="text-center bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 border-2 border-gray-200 shadow-inner">
                <div className="flex items-center justify-center space-x-2 text-gray-500 mb-4">
                  <SafeIcon icon={FiLock} />
                  <span className="text-xs uppercase tracking-wide font-bold">Door Access Code</span>
                </div>
                <div className="text-6xl font-mono font-bold text-gray-900 tracking-widest mb-4 bg-white py-6 rounded-xl shadow-md border-2 border-gray-300">
                  {guestPage.doorCode || '----'}
                </div>
                <button
                  onClick={() => copyToClipboard(guestPage.doorCode, 'Door code')}
                  className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  <SafeIcon icon={copiedText === 'Door code' ? FiCheck : FiCopy} />
                  <span>{copiedText === 'Door code' ? 'Copied!' : 'Copy Code'}</span>
                </button>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <p className="text-xs text-gray-500 uppercase font-bold mb-2 flex items-center">
                  <SafeIcon icon={FiMapPin} className="mr-1.5" />
                  Property Address
                </p>
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <p className="text-gray-900 text-lg font-medium leading-snug">{property.address}</p>
                </div>
              </div>
              
              <div className="bg-blue-50 rounded-xl p-1.5 border-2 border-blue-200">
                {!showMapOptions ? (
                  <button 
                    onClick={() => setShowMapOptions(true)}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg flex items-center justify-center space-x-2 transition-all shadow-md"
                  >
                    <SafeIcon icon={FiMap} />
                    <span>Get Directions</span>
                  </button>
                ) : (
                  <div 
                    className="p-3 space-y-2"
                  >
                    <div className="grid grid-cols-3 gap-2">
                      <button onClick={() => openMap('apple')} className="flex flex-col items-center justify-center p-3 bg-white rounded-lg shadow-sm hover:bg-gray-50 border border-gray-200 transition-all hover:scale-105">
                        <SafeIcon icon={SiApple} className="text-2xl mb-1 text-gray-900" />
                        <span className="text-xs font-medium text-gray-600">Apple Maps</span>
                      </button>
                      <button onClick={() => openMap('google')} className="flex flex-col items-center justify-center p-3 bg-white rounded-lg shadow-sm hover:bg-gray-50 border border-gray-200 transition-all hover:scale-105">
                        <SafeIcon icon={SiGooglemaps} className="text-2xl text-blue-600 mb-1" />
                        <span className="text-xs font-medium text-gray-600">Google Maps</span>
                      </button>
                      <button onClick={() => openMap('waze')} className="flex flex-col items-center justify-center p-3 bg-white rounded-lg shadow-sm hover:bg-gray-50 border border-gray-200 transition-all hover:scale-105">
                        <SafeIcon icon={SiWaze} className="text-2xl text-blue-400 mb-1" />
                        <span className="text-xs font-medium text-gray-600">Waze</span>
                      </button>
                    </div>
                    <button 
                      onClick={() => setShowMapOptions(false)} 
                      className="w-full text-center text-xs text-gray-500 hover:text-gray-700 py-2 font-medium"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </SectionCard>

        {/* 3. WiFi Section */}
        <SectionCard id="wifi" title="WiFi Connection" icon={FiWifi}>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="relative group p-6 rounded-xl border-2 border-gray-200 bg-gradient-to-br from-gray-50 to-white hover:border-blue-300 transition-all">
              <p className="text-xs text-gray-500 uppercase font-bold mb-3 flex items-center">
                <SafeIcon icon={FiWifi} className="mr-2 text-blue-600" />
                Network Name (SSID)
              </p>
              <p className="text-2xl font-semibold text-gray-900 break-all font-mono">{property.wifiName || 'Not available'}</p>
              {property.wifiName && (
                <button 
                  onClick={() => copyToClipboard(property.wifiName, 'WiFi name')}
                  className="absolute top-4 right-4 text-gray-400 hover:text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <SafeIcon icon={copiedText === 'WiFi name' ? FiCheck : FiCopy} />
                </button>
              )}
            </div>
            <div className="relative group p-6 rounded-xl border-2 border-gray-200 bg-gradient-to-br from-gray-50 to-white hover:border-blue-300 transition-all">
              <p className="text-xs text-gray-500 uppercase font-bold mb-3 flex items-center">
                <SafeIcon icon={FiLock} className="mr-2 text-blue-600" />
                Password
              </p>
              <p className="text-2xl font-mono font-semibold text-gray-900 break-all select-all">{property.wifiPassword || 'Not available'}</p>
              {property.wifiPassword && (
                <button 
                  onClick={() => copyToClipboard(property.wifiPassword, 'WiFi password')}
                  className="absolute top-4 right-4 text-gray-400 hover:text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <SafeIcon icon={copiedText === 'WiFi password' ? FiCheck : FiCopy} />
                </button>
              )}
            </div>
          </div>
        </SectionCard>

        {/* 4. House Rules Section */}
        <SectionCard id="rules" title="House Rules & Guidelines" icon={FiBook}>
          <div className="space-y-6">
            {property.houseRules && (
              <div>
                <h3 className="font-bold text-gray-900 mb-4 flex items-center text-base">
                  <span className="w-2 h-2 rounded-full bg-blue-500 mr-2"></span>
                  Welcome & General Guidelines
                </h3>
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                  <div className="text-gray-700 leading-relaxed space-y-3">
                    {renderParsedContent(parseTextContent(property.houseRules))}
                  </div>
                </div>
              </div>
            )}
            
            {property.checkoutProcedures && (
              <div>
                <h3 className="font-bold text-gray-900 mb-4 flex items-center text-base">
                  <span className="w-2 h-2 rounded-full bg-red-500 mr-2"></span>
                  Checkout Procedures
                </h3>
                <InfoCard color="red" className="border-l-4 border-red-400">
                  <div className="text-gray-700 leading-relaxed space-y-3">
                    {renderParsedContent(parseTextContent(property.checkoutProcedures))}
                  </div>
                </InfoCard>
              </div>
            )}
          </div>
        </SectionCard>

        {/* 5. Property Guide Section */}
        <SectionCard id="guide" title="Property Guide & Amenities" icon={FiStar}>
          <div className="space-y-4">
            {property.tvInstructions && (() => {
              const tvContent = typeof property.tvInstructions === 'string' 
                ? property.tvInstructions 
                : property.tvInstructions.content || '';
              const tvTitle = typeof property.tvInstructions === 'object' && property.tvInstructions.title
                ? property.tvInstructions.title
                : 'TV & Entertainment';
              return tvContent ? (
                <CollapsibleSection 
                  id="tv" 
                  title={tvTitle}
                  icon={FiTv}
                  defaultExpanded={false}
                >
                  <div className="text-gray-700 text-sm leading-relaxed">
                    {renderParsedContent(parseTextContent(tvContent))}
                  </div>
                </CollapsibleSection>
              ) : null;
            })()}
            
            {property.fireplaceInstructions && (() => {
              const fpContent = typeof property.fireplaceInstructions === 'string' 
                ? property.fireplaceInstructions 
                : property.fireplaceInstructions.content || '';
              const fpTitle = typeof property.fireplaceInstructions === 'object' && property.fireplaceInstructions.title
                ? property.fireplaceInstructions.title
                : 'Fireplace Instructions';
              return fpContent ? (
                <CollapsibleSection 
                  id="fireplace" 
                  title={fpTitle}
                  icon={FiThermometer}
                  defaultExpanded={false}
                >
                  <div className="text-gray-700 text-sm leading-relaxed space-y-4">
                    {renderParsedContent(parseTextContent(fpContent))}
                  </div>
                </CollapsibleSection>
              ) : null;
            })()}
            
            {property.additionalInstructions && property.additionalInstructions.length > 0 && property.additionalInstructions.map((instruction, index) => (
              instruction.title && instruction.content && (
                <CollapsibleSection 
                  key={index}
                  id={`additional-${index}`}
                  title={instruction.title}
                  icon={FiInfo}
                  defaultExpanded={false}
                >
                  <div className="text-gray-700 text-sm leading-relaxed">
                    {renderParsedContent(parseTextContent(instruction.content))}
                  </div>
                </CollapsibleSection>
              )
            ))}
            
            {!property.tvInstructions && !property.fireplaceInstructions && (!property.additionalInstructions || property.additionalInstructions.length === 0) && (
              <div className="text-center py-8">
                <SafeIcon icon={FiInfo} className="text-gray-300 text-4xl mx-auto mb-3" />
                <p className="text-gray-400 italic">No additional guide information available for this property.</p>
              </div>
            )}
          </div>
        </SectionCard>

        {/* 6. Local Recommendations Section */}
        {property.localRecommendations && (
          <SectionCard id="recommendations" title="Local Recommendations" icon={FiMapPin}>
            <div className="text-gray-700 text-sm leading-relaxed">
              {property.localRecommendations ? (
                <div 
                  className="text-gray-900 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: property.localRecommendations }}
                />
              ) : (
                <p className="text-gray-500">Local recommendations not available.</p>
              )}
            </div>
          </SectionCard>
        )}

        {/* 7. Support Section */}
        <SectionCard id="support" title="Support & Contact" icon={FiPhone}>
          <div className="space-y-4">
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border-2 border-green-200">
              <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                <SafeIcon icon={FiPhone} className="mr-2 text-green-600" />
                Need Help During Your Stay?
              </h3>
              <p className="text-gray-600 text-sm mb-4">Contact us for any questions, maintenance issues, or emergencies.</p>
              <div className="bg-white rounded-lg p-4 border border-green-200">
                {property.supportContact ? (
                  <div 
                    className="text-gray-900 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: property.supportContact }}
                  />
                ) : (
                  <p className="text-gray-500">Contact information not available.</p>
                )}
              </div>
            </div>
          </div>
        </SectionCard>

        {/* Footer */}
        <div className="text-center pt-8 pb-4">
          <p className="text-xs text-gray-400">Powered by RentalPortal</p>
        </div>

      </div>
    </div>
  );
};

export default PortalContent;
